import { createListApiSchema } from '../General/schemas';
import { z } from 'zod';
import { languageCodeSchema } from '../Language/schemas';
// ── Shared primitives ────────────────────────────────────────────────

const optionValueSchema = z.object({
	value: z.union([z.string(), z.record(z.unknown())]).optional(),
	label: z.string().optional(),
});

const tableColumnSchema = z.object({
	field: z.string(),
	headerName: z.string(),
});

const documentRefSchema = z
	.object({
		ownerType: z.string().optional(),
		mimeType: z.string().optional(),
		fileName: z.string().optional(),
		status: z.string().optional(),
		reviewReason: z.array(z.unknown()).optional(),
		bytes: z.number().optional(),
		type: z.string().optional(),
		archived: z.boolean().optional(),
		hasBeenReviewed: z.boolean().optional(),
		_id: z.string().optional(),
		id: z.string().optional(),
		createdAt: z.string().optional(),
		updatedAt: z.string().optional(),
	})
	.passthrough();

const navigationSchema = z.object({
	previousLabel: z.string().optional(),
	nextLabel: z.string().optional(),
	submitLabel: z.string().optional(),
});

const socSecurityOptionsSchema = z.object({
	optionalA1: z.boolean().optional(),
	allowLimosaException: z.boolean().optional(),
	ignoreDimona: z.boolean().optional(),
	allowIndependenceException: z.boolean().optional(),
	acceptA1Request: z.boolean().optional(),
});

const workActivitySchema = z.object({
	label: z.string().optional(),
	questionText: z.string().optional(),
	requiredDocumentTypeIds: z.array(z.union([z.string(), z.array(z.string())])).optional(),
});

// ── Field schema (flat union of all field properties) ────────────────

export const fieldSchema = z.object({
	type: z.string(),
	name: z.string().optional(),
	id: z.string().optional(),
	key: z.string().optional(),
	label: z.string().optional(),
	category: z.string().optional(),
	variant: z.string().optional(),
	required: z.union([z.boolean(), z.string()]).optional(),
	helperText: z.string().optional(),
	placeholder: z.string().optional(),
	fullWidth: z.boolean().optional(),
	errorMessages: z.record(z.string()).optional(),
	validators: z.array(z.unknown()).optional(),
	sx: z.record(z.unknown()).optional(),
	rowLabel: z.string().optional(),
	forceDisabled: z.boolean().optional(),
	// Typography / Markdown / ScrollableContentViewer
	children: z.string().nullable().optional(),
	content: z.string().optional(),
	useHtml: z.boolean().optional(),
	// TextField
	value: z.string().optional(),
	// PhoneField
	defaultCountry: z.string().optional(),
	allowFixedLine: z.boolean().optional(),
	// DatePicker
	isEndOfDay: z.boolean().optional(),
	// CheckGroup / RadioGroup / AutoComplete / QuestionMultipleChoice
	options: z.array(z.union([optionValueSchema, z.string()])).optional(),
	answers: z.array(optionValueSchema).optional(),
	multiple: z.union([z.boolean(), z.string()]).optional(),
	optionLabel: z.string().optional(),
	// AsyncAutoComplete
	dataSource: z.string().optional(),
	filter: z.union([z.string(), z.record(z.unknown())]).optional(),
	// Agree
	termsUrl: z.string().optional(),
	termsLabel: z.string().optional(),
	termsUrlText: z.string().optional(),
	// Link / Image / VideoPlayer
	url: z.string().optional(),
	image: z.union([z.string(), documentRefSchema]).optional(),
	// Table
	columns: z.array(tableColumnSchema).optional(),
	// UserApproval
	roleIds: z.array(z.string()).optional(),
});

// ── Section schema (flat union of all section properties) ────────────

const settingsColumnSchema = z.object({
	fields: z.array(fieldSchema).optional(),
});

const categoryColumnSchema = z.object({
	label: z.string().optional(),
	value: z.string().optional(),
	width: z.number().optional(),
});

const rowSchema = z.object({
	label: z.string().optional(),
	subLabel: z.string().optional(),
	header: z.string().optional(),
	info: z.string().optional(),
});
const categorySchema = z.object({
	label: z.string().optional(),
	name: z.string().optional(),
	columns: z.array(categoryColumnSchema).optional(),
});

export const tabSchema = z.object({
	id: z.string().optional(),
	label: z.string().optional(),
	color: z.string().optional(),
	columns: z.array(settingsColumnSchema).optional(),
	fields: z.array(fieldSchema).optional(),
	categories: z.array(categorySchema).optional(),
	rows: z.array(rowSchema).optional(),
});

export const sectionSchema = z.object({
	type: z.string().optional(),
	id: z.string().optional(),
	name: z.string().optional(),
	title: z.string().optional(),
	fields: z.array(fieldSchema).optional(),
	navigation: navigationSchema.nullable().optional(),
	refDataMap: z.record(z.string()).optional(),
	disabled: z.boolean().optional(),
	possibleDocuments: z.array(z.union([z.string(), z.array(z.string())])).optional(),
	requiredDocuments: z.array(z.union([z.string(), z.array(z.string())])).optional(),
	documentTypeForMultipleTypes: z.string().optional(),
	noWelcome: z.boolean().optional(),
	acceptedTypes: z.union([z.array(z.string()), z.string()]).optional(),
	// VCASection
	vcaType: z.string().optional(),
	useCamera: z.boolean().optional(),
	cameraIncludes: z.union([z.array(z.string()), z.string()]).optional(),
	cameraRotationAngle: z.number().optional(),
	// SocSecurity*
	options: socSecurityOptionsSchema.optional(),
	// SafetyCertificates* / FieldSection
	textOverride: z.record(z.string()).optional(),
	possibleDocumentTypes: z.array(z.string()).optional(),
	requiredDocumentTypes: z.array(z.array(z.string())).optional(),
	showTypesInSubtitle: z.boolean().optional(),
	// WorkTypeSafetyCertificatesSection
	workActivities: z.array(workActivitySchema).nullable().optional(),
	// TestSection
	minimalScore: z.union([z.number(), z.string()]).optional(),
	maxAttempts: z.union([z.number(), z.string()]).optional(),
	fieldsToDisplay: z.union([z.number(), z.string()]).optional(),
	// SettingsSection
	labelWidth: z.number().optional(),
	padding: z.number().optional(),
	columns: z.array(settingsColumnSchema).optional(),
	tabs: z.array(tabSchema).optional(),
	// FieldSection
	labels: z.array(z.unknown()).nullable().optional(),
	// SCORM / NonSkippableVideo
	url: z.string().optional(),
	questionnaireUrl: z.string().optional(),
	// GapAnalysisSection
	defaultGapAnalysisId: z.string().optional(),
	gapAnalysisEnabled: z.boolean().optional(),
	style: z.record(z.unknown()).optional(),
});

// ── Form schema ──────────────────────────────────────────────────────

const formSchemaObjectSchema = z.object({
	sections: z.array(sectionSchema).optional(),
});

const formSchemaSchema = formSchemaObjectSchema.passthrough().nullable().optional();

// ── Translations ─────────────────────────────────────────────────────

export const translationFieldSchema = z.object({
	name: z.string().optional(),
	id: z.string().optional(),
	label: z.string().optional(),
	helperText: z.string().optional(),
	children: z.string().optional(),
	termsUrl: z.string().optional(),
	termsLabel: z.string().optional(),
	termsUrlText: z.string().optional(),
	allowFixedLine: z.boolean().optional(),
	content: z.string().optional(),
	url: z.string().optional(),
	placeholder: z.string().optional(),
	options: z.array(optionValueSchema).optional(),
	answers: z.array(optionValueSchema).optional(),
});

export const translationSectionSchema = z.object({
	name: z.string().optional(),
	id: z.string().optional(),
	title: z.string().optional(),
	fields: z.array(translationFieldSchema).optional(),
	options: socSecurityOptionsSchema.optional(),
	navigation: navigationSchema.optional(),
	textOverride: z.record(z.string()).optional(),
});

const translationValueSchema = z
	.object({
		title: z.string().optional(),
		keywords: z.string().optional(),
		sections: z.array(translationSectionSchema).optional(),
		formSchema: z.object({ sections: z.array(translationSectionSchema) }).optional(),
	})
	.nullable();

const translationsSchema = z.record(languageCodeSchema, translationValueSchema).optional();

// ── Top-level form document ──────────────────────────────────────────

const formTypeSchema = z.enum([
	'custom',
	'simple',
	'simple-agentic',
	'identification',
	'account',
	'contact',
	'group',
	'group-task',
	'user',
	'training',
	'event',
	'approval',
	'playit',
	'protected',
	'work-permit',
	'TestSection',
]);

export const formSchema = z.object({
	id: z.string(),
	title: z.string(),
	type: formTypeSchema,
	slug: z.string(),
	// should not be necessary anymore?
	version: z.number(),
	isDeleted: z.boolean(),
	saveDrafts: z.boolean().optional(),
	description: z.string().optional(),
	// TODO: can one of these two be cut?
	language: languageCodeSchema.optional(),
	availableLanguages: z.array(languageCodeSchema).optional(),
	keywords: z.string().optional(),
	formSchema: formSchemaSchema,
	translations: translationsSchema,
	translationStatus: z.enum(['idle', 'translating']).optional(),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
	selectedLanguage: languageCodeSchema.optional(),
});

export const formsApiResponseSchema = createListApiSchema(formSchema);
